import { exportWithSchema } from './excelExporter';
import {
    formatDateTime,
    pdfStylesBase,
    pdfDefaultStyle,
    buildVehiculosTable,
} from './pdfVehiculosCore';
import { getExportTimestamp } from '../formatters/dates';

const pdfMake = require('pdfmake/build/pdfmake.js');
const pdfFonts = require('pdfmake/build/vfs_fonts.js');
pdfMake.vfs = pdfFonts.pdfMake.vfs;

// --- HELPERS COMPARTIDOS ---
const calculateRendimiento = (dist, lit) => {
    if (dist > 0 && lit > 0) {
        return (dist / lit).toFixed(2);
    }
    return '-';
};

// --- CONFIGURACIÓN DE EXCEL ---
const TRACEABILITY_SCHEMA = {
    vin: { header: 'VIN', width: 20, value: row => row.vin || row.vehiculo?.vin },
    marca: { header: 'Marca', width: 15, value: row => row.vehiculo?.marca },
    modelo: { header: 'Modelo', width: 25, value: row => `${row.vehiculo?.familia || ''} ${row.vehiculo?.catalogo || ''}`.trim() },
    origen: { header: 'Origen', width: 20, value: row => row.origen?.patio_nombre || '-' },
    // Fechas separadas
    fecha_salida: { header: 'Fecha Salida', width: 18, type: 'datetime', value: row => row.origen?.fecha },
    destino: { header: 'Destino', width: 20, value: row => row.destino?.patio_nombre || '-' },
    fecha_llegada: { header: 'Fecha Llegada', width: 18, type: 'datetime', value: row => row.destino?.fecha },
    distancia: { header: 'Distancia (km)', width: 15, value: row => row.distancia_km },
    consumo: { header: 'Consumo (L)', width: 15, value: row => row.litros_consumidos },
    // Calcular rendimiento si falta
    rendimiento: { header: 'Rendimiento (km/L)', width: 18, value: row => row.rendimiento_real || calculateRendimiento(row.distancia_km, row.litros_consumidos) },
};

const TRACEABILITY_COLUMNS = [
    'vin', 'marca', 'modelo', 'origen', 'fecha_salida', 'destino', 'fecha_llegada', 'distancia', 'consumo', 'rendimiento'
];

export async function exportTraceabilityExcel(segments, filters) {
    const filename = `Trazabilidad_${filters.startMonth}${filters.startYear}_${filters.endMonth}${filters.endYear}_${getExportTimestamp()}`;

    await exportWithSchema({
        rows: segments,
        visibleColumns: TRACEABILITY_COLUMNS,
        schema: TRACEABILITY_SCHEMA,
        filename,
        sheetName: 'Trazabilidad',
        title: `Reporte de Trazabilidad: ${filters.startMonth} ${filters.startYear} - ${filters.endMonth} ${filters.endYear}`
    });
}

// --- CONFIGURACIÓN DE PDF ---
// --- CONFIGURACIÓN DE PDF ---
export function exportTraceabilityPDF(segments, filters, chartImages = {}) {
    try {
        const filename = `Trazabilidad_${filters.startMonth}${filters.startYear}_${filters.endMonth}${filters.endYear}_${getExportTimestamp()}`;
        const period = `${filters.startMonth} ${filters.startYear} - ${filters.endMonth} ${filters.endYear}`;

        // Calcular Resúmenes
        const totalDistance = segments.reduce((acc, seg) => acc + (seg.distancia_km || 0), 0).toFixed(2);
        const totalConsumption = segments.reduce((acc, seg) => acc + (seg.litros_consumidos || 0), 0).toFixed(2);
        const totalMovements = segments.length;

        // Columnas faltantes en PDF
        const PDF_COLUMNS = ['vin', 'marca', 'origen', 'f_salida', 'destino', 'f_llegada', 'dist', 'cons', 'rend'];

        // Aplanar filas
        const flatRows = segments.map(seg => ({
            vin: seg.vin || seg.vehiculo?.vin,
            marca: seg.vehiculo?.marca,
            origen: seg.origen?.patio_nombre || '-',
            f_salida: seg.origen?.fecha,
            destino: seg.destino?.patio_nombre || '-',
            f_llegada: seg.destino?.fecha,
            dist: seg.distancia_km ? `${seg.distancia_km} km` : '-',
            cons: seg.litros_consumidos ? `${seg.litros_consumidos} L` : '-',
            rend: seg.rendimiento_real || calculateRendimiento(seg.distancia_km, seg.litros_consumidos),
        }));

        const PDF_SCHEMA_MAP = {
            vin: { header: 'VIN' },
            marca: { header: 'Marca' },
            origen: { header: 'Origen' },
            f_salida: { header: 'Salida', type: 'datetime' },
            destino: { header: 'Destino' },
            f_llegada: { header: 'Llegada', type: 'datetime' },
            dist: { header: 'Dist.' },
            cons: { header: 'Litros' },
            rend: { header: 'km/L' },
        };

        const headerRow = PDF_COLUMNS.map(col => PDF_SCHEMA_MAP[col].header);
        const bodyRows = flatRows.map(row =>
            PDF_COLUMNS.map(col => {
                const val = row[col];
                if (PDF_SCHEMA_MAP[col].type === 'datetime') return formatDateTime(val).split(' ')[0]; // ¿Mostrar solo fecha para ahorrar espacio? O solo fuente más pequeña. Probemos fecha y hora completa pero pequeña.
                return val || '-';
            })
        );

        // --- SIMULACIONES DATOS DE TABLA RESUMEN (No reemplaza las gráficas, pero se mantiene por si acaso) ---
        // (El código original de tablas de distancia/consumo se mantiene arriba si se decide usar, 
        // pero aquí nos enfocamos en agregar las imágenes reales)

        const docDefinition = {
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [20, 60, 20, 30], // Márgenes más pequeños para ajustar más columnas
            header: (currentPage, pageCount) => ({
                columns: [
                    {
                        stack: [
                            { text: 'Reporte de Trazabilidad', style: 'title', color: '#1F2937' },
                            { text: `Periodo: ${period}`, fontSize: 10, color: '#4B5563', margin: [0, 2, 0, 0] },
                        ],
                        margin: [20, 20, 0, 0],
                    },
                    {
                        text: `Página ${currentPage} de ${pageCount}`,
                        alignment: 'right', fontSize: 8, color: '#6B7280', margin: [0, 25, 20, 0],
                    },
                ],
            }),
            content: [
                // Tarjetas de Resumen
                {
                    columns: [
                        buildSummaryCard('Total Movimientos', totalMovements),
                        buildSummaryCard('Distancia Total', `${totalDistance} km`),
                        buildSummaryCard('Consumo Total', `${totalConsumption} L`),
                    ],
                    columnGap: 10,
                    margin: [0, 0, 0, 16]
                },

                // Tabla Detallada
                { text: 'Detalle de Movimientos', style: 'subheader', margin: [0, 0, 0, 5] },
                buildVehiculosTable(headerRow, bodyRows, 0),

                // NUEVA SECCIÓN: GRÁFICAS (Solo si se pasan las imágenes)
                ...(chartImages && (chartImages.distanceChart || chartImages.consumptionChart) ? [
                    { text: 'Gráficas de Análisis', style: 'subheader', margin: [0, 20, 0, 10], pageBreak: 'before' },
                    {
                        columns: [
                            chartImages.distanceChart ? {
                                stack: [
                                    { text: 'Distancia por Mes', style: 'label', margin: [0, 0, 0, 5], alignment: 'center' },
                                    { image: chartImages.distanceChart, width: 380, alignment: 'center' }
                                ],
                                width: '50%'
                            } : {},
                            chartImages.consumptionChart ? {
                                stack: [
                                    { text: 'Consumo por Mes', style: 'label', margin: [0, 0, 0, 5], alignment: 'center' },
                                    { image: chartImages.consumptionChart, width: 380, alignment: 'center' }
                                ],
                                width: '50%'
                            } : {}
                        ],
                        columnGap: 10
                    }
                ] : [])
            ],
            styles: {
                ...pdfStylesBase,
                tableCell: { fontSize: 7 }, // Fuente más pequeña para tabla grande
                tableHeader: { bold: true, fontSize: 8, color: 'black' },
                label: { fontSize: 10, bold: true, color: '#4B5563' }
            },
            defaultStyle: pdfDefaultStyle,
        };

        pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);

    } catch (error) {
        console.error('Error generating PDF:', error);
    }
}

function buildSummaryCard(title, value) {
    return {
        stack: [
            { text: title, fontSize: 9, color: '#6B7280', bold: true },
            { text: String(value), fontSize: 14, color: '#111827', bold: true, margin: [0, 2, 0, 0] }
        ],
        fillColor: '#F3F4F6',
        margin: [0, 0, 0, 0],
        padding: 8
    };
}
